"use client";

import { useState, useEffect, useCallback, useRef } from "react";

const MICROPHONE_SETUP_TIMEOUT_MS = 10000;
const REALTIME_SESSION_TIMEOUT_MS = 12000;

function getUserMediaWithTimeout(constraints) {
  let timedOut = false;
  let timeoutId;
  const mediaRequest = navigator.mediaDevices
    .getUserMedia(constraints)
    .then((stream) => {
      if (timedOut) {
        stream.getTracks().forEach((track) => track.stop());
        throw new Error("Microphone permission timed out");
      }
      return stream;
    });

  return Promise.race([
    mediaRequest,
    new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        timedOut = true;
        reject(new Error("Microphone permission timed out"));
      }, MICROPHONE_SETUP_TIMEOUT_MS);
    }),
  ]).finally(() => clearTimeout(timeoutId));
}

export const useVoiceSearch = () => {
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [listeningText, setListeningText] = useState("");
  const [isVoiceQuery, setIsVoiceQuery] = useState(false);
  const [autoPlayResponse, setAutoPlayResponse] = useState(false);
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const isMountedRef = useRef(true);
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const localStreamRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const isCaptureActiveRef = useRef(false);
  const remoteAudioElementRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioPrimedRef = useRef(false);
  const connectPromiseRef = useRef(null);
  const onVoiceStartRef = useRef(null);
  const onResultRef = useRef(null);
  const latestTranscriptRef = useRef("");
  const transcriptDeltaBufferRef = useRef("");
  const emittedTranscriptRef = useRef(false);
  const suppressPendingResultRef = useRef(false);
  const endEmitOnlyRef = useRef(false);
  const listenWindowTimerRef = useRef(null);
  const speechQueueRef = useRef([]);
  const lastRemoteAudioPlayAtRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      isCaptureActiveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hasWebRtc = typeof window.RTCPeerConnection !== "undefined";
    const hasMediaDevices = Boolean(navigator?.mediaDevices?.getUserMedia);
    setSpeechSupported(hasWebRtc && hasMediaDevices);

    return () => {
      if (listenWindowTimerRef.current) {
        clearTimeout(listenWindowTimerRef.current);
        listenWindowTimerRef.current = null;
      }

      const dc = dataChannelRef.current;
      if (dc) {
        try {
          dc.close();
        } catch {}
      }
      dataChannelRef.current = null;

      speechQueueRef.current.forEach((waiter) => {
        if (waiter && typeof waiter.resolve === "function") {
          waiter.resolve(false);
        }
      });
      speechQueueRef.current = [];

      const pc = peerConnectionRef.current;
      if (pc) {
        try {
          pc.getSenders().forEach((sender) => sender.track?.stop());
        } catch {}
        try {
          pc.close();
        } catch {}
      }
      peerConnectionRef.current = null;

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      localStreamRef.current = null;
      localAudioTrackRef.current = null;

      const audioEl = remoteAudioElementRef.current;
      if (audioEl) {
        try {
          audioEl.pause();
          audioEl.srcObject = null;
          audioEl.remove();
        } catch {}
      }
      remoteAudioElementRef.current = null;
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch {}
      }
      audioContextRef.current = null;
      audioPrimedRef.current = false;
      setIsAssistantSpeaking(false);
    };
  }, []);

  const setupVoiceRecognition = useCallback((onResult) => {
    onResultRef.current = onResult;
  }, []);

  const primeAudioPlayback = useCallback(async () => {
    if (typeof window === "undefined") return false;
    if (audioPrimedRef.current) return true;

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioCtx();
        }
        if (audioContextRef.current.state === "suspended") {
          await audioContextRef.current.resume();
        }
      }

      const unlockAudio = document.createElement("audio");
      unlockAudio.playsInline = true;
      unlockAudio.muted = true;
      unlockAudio.src =
        "data:audio/mp3;base64,//uQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAABAAADpAAACwAATGF2ZjU4LjI5LjEwMAAAAAAAAAAAAAAA//uQxAADBQABpAAAAnEAAANIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA";
      document.body.appendChild(unlockAudio);
      try {
        await unlockAudio.play();
      } catch {}
      try {
        unlockAudio.pause();
        unlockAudio.currentTime = 0;
      } catch {}
      unlockAudio.remove();

      audioPrimedRef.current = true;
      return true;
    } catch {
      return false;
    }
  }, []);

  const emitTranscriptIfNeeded = useCallback(
    (transcript) => {
      const cleanTranscript = String(transcript || "").trim();
      if (!cleanTranscript) return;
      if (emittedTranscriptRef.current) return;
      if (typeof onResultRef.current !== "function") return;

      emittedTranscriptRef.current = true;
      setIsVoiceQuery(true);
      setAutoPlayResponse(true);
      onResultRef.current(cleanTranscript);
    },
    [setAutoPlayResponse, setIsVoiceQuery],
  );

  const stopVoiceCapture = useCallback(
    (options = {}) => {
      const { suppressPendingResult = true } = options;
      isCaptureActiveRef.current = false;
      onVoiceStartRef.current = null;
      suppressPendingResultRef.current = suppressPendingResult;

      if (listenWindowTimerRef.current) {
        clearTimeout(listenWindowTimerRef.current);
        listenWindowTimerRef.current = null;
      }

      const localTrack = localAudioTrackRef.current;
      if (localTrack) {
        localTrack.enabled = false;
      }

      const dc = dataChannelRef.current;
      if (dc && dc.readyState === "open") {
        try {
          dc.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
        } catch {}
      }

      if (!suppressPendingResultRef.current) {
        emitTranscriptIfNeeded(latestTranscriptRef.current);
      }

      transcriptDeltaBufferRef.current = "";
      setIsListening(false);
      setListeningText("");
      suppressPendingResultRef.current = false;
      endEmitOnlyRef.current = false;
    },
    [emitTranscriptIfNeeded],
  );

  const extractTranscriptFromEvent = useCallback((event) => {
    if (!event || typeof event !== "object") return "";

    if (typeof event.transcript === "string") {
      return event.transcript;
    }

    if (typeof event.delta === "string") {
      return event.delta;
    }

    if (typeof event.text === "string") {
      return event.text;
    }

    const item = event.item;
    if (item && Array.isArray(item.content)) {
      for (const part of item.content) {
        if (typeof part?.transcript === "string") return part.transcript;
        if (typeof part?.text === "string") return part.text;
      }
    }

    return "";
  }, []);

  const handleRealtimeEvent = useCallback(
    (event) => {
      const eventType = String(event?.type || "");

      if (eventType === "response.done") {
        const nextWaiter = speechQueueRef.current.shift();
        if (nextWaiter && typeof nextWaiter.resolve === "function") {
          nextWaiter.resolve(true);
        }
      }

      const isUserTranscriptEvent = eventType.includes(
        "input_audio_transcription",
      );
      if (!isUserTranscriptEvent || !isCaptureActiveRef.current) return;

      // Realtime often streams transcript as tiny deltas; buffer them and emit only
      // when a completed/full transcript event is received.
      if (eventType.endsWith(".delta")) {
        const delta = String(event?.delta || "");
        if (!delta) return;
        transcriptDeltaBufferRef.current += delta;
        latestTranscriptRef.current = transcriptDeltaBufferRef.current.trim();
        return;
      }

      const completedTranscript = String(
        event?.transcript ||
          extractTranscriptFromEvent(event) ||
          transcriptDeltaBufferRef.current ||
          "",
      ).trim();

      if (!completedTranscript) return;

      latestTranscriptRef.current = completedTranscript;
      transcriptDeltaBufferRef.current = "";

      if (!endEmitOnlyRef.current) {
        emitTranscriptIfNeeded(completedTranscript);
        setIsListening(false);
        setListeningText("");
      }
    },
    [emitTranscriptIfNeeded, extractTranscriptFromEvent],
  );

  const connectRealtimeSession = useCallback(async () => {
    const existingPc = peerConnectionRef.current;
    const existingDc = dataChannelRef.current;

    if (
      existingPc &&
      existingDc &&
      existingDc.readyState !== "closed" &&
      existingPc.connectionState !== "closed" &&
      existingPc.connectionState !== "failed"
    ) {
      return;
    }

    if (
      existingPc &&
      (existingDc?.readyState === "closed" ||
        existingPc.connectionState === "closed" ||
        existingPc.connectionState === "failed")
    ) {
      try {
        existingPc.getSenders().forEach((sender) => sender.track?.stop());
      } catch {}
      try {
        existingPc.close();
      } catch {}
      peerConnectionRef.current = null;
      dataChannelRef.current = null;

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      localStreamRef.current = null;
      localAudioTrackRef.current = null;
    }

    if (connectPromiseRef.current) {
      await connectPromiseRef.current;
      return;
    }

    let pendingPeerConnection = null;
    let pendingDataChannel = null;
    let pendingStream = null;
    let pendingAudioElement = null;
    connectPromiseRef.current = (async () => {
      const pc = new RTCPeerConnection();
      pendingPeerConnection = pc;

      const existingAudio = remoteAudioElementRef.current;
      if (existingAudio) {
        try {
          existingAudio.pause();
          existingAudio.srcObject = null;
          existingAudio.remove();
        } catch {}
      }

      const remoteAudio = document.createElement("audio");
      pendingAudioElement = remoteAudio;
      remoteAudio.autoplay = true;
      remoteAudio.playsInline = true;
      remoteAudio.muted = false;
      remoteAudio.volume = 1;
      remoteAudio.style.display = "none";
      remoteAudio.addEventListener("playing", () => {
        lastRemoteAudioPlayAtRef.current = Date.now();
        setIsAssistantSpeaking(true);
      });
      remoteAudio.addEventListener("pause", () => {
        setIsAssistantSpeaking(false);
      });
      remoteAudio.addEventListener("ended", () => {
        setIsAssistantSpeaking(false);
      });
      remoteAudio.addEventListener("emptied", () => {
        setIsAssistantSpeaking(false);
      });
      remoteAudio.addEventListener("timeupdate", () => {
        if (remoteAudio.currentTime > 0) {
          lastRemoteAudioPlayAtRef.current = Date.now();
        }
      });
      document.body.appendChild(remoteAudio);
      remoteAudioElementRef.current = remoteAudio;

      pc.ontrack = (e) => {
        remoteAudio.srcObject = e.streams[0];
        remoteAudio.play().catch(() => {});
      };

      const dc = pc.createDataChannel("oai-events");
      pendingDataChannel = dc;
      dc.addEventListener("message", (e) => {
        try {
          const parsed = JSON.parse(e.data);
          handleRealtimeEvent(parsed);
        } catch {}
      });

      const stream = await getUserMediaWithTimeout({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      pendingStream = stream;

      const [audioTrack] = stream.getAudioTracks();
      if (!audioTrack) {
        throw new Error("No audio track available");
      }

      audioTrack.enabled = false;
      pc.addTrack(audioTrack, stream);

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const response = await fetch("/api/search/realtime/session", {
        method: "POST",
        headers: { "Content-Type": "application/sdp" },
        body: offer.sdp,
        signal: AbortSignal.timeout(REALTIME_SESSION_TIMEOUT_MS),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Realtime session failed: ${response.status} ${errorText}`,
        );
      }

      const answerSdp = await response.text();
      await pc.setRemoteDescription({
        type: "answer",
        sdp: answerSdp,
      });

      peerConnectionRef.current = pc;
      dataChannelRef.current = dc;
      localStreamRef.current = stream;
      localAudioTrackRef.current = audioTrack;
    })();

    try {
      await connectPromiseRef.current;
    } catch (error) {
      try {
        pendingDataChannel?.close();
      } catch {}
      try {
        pendingPeerConnection?.close();
      } catch {}
      pendingStream?.getTracks().forEach((track) => track.stop());
      if (pendingAudioElement) {
        try {
          pendingAudioElement.pause();
          pendingAudioElement.srcObject = null;
          pendingAudioElement.remove();
        } catch {}
      }
      if (remoteAudioElementRef.current === pendingAudioElement) {
        remoteAudioElementRef.current = null;
      }
      peerConnectionRef.current = null;
      dataChannelRef.current = null;
      localStreamRef.current = null;
      localAudioTrackRef.current = null;
      throw error;
    } finally {
      connectPromiseRef.current = null;
    }
  }, [handleRealtimeEvent]);

  const waitForDataChannelOpen = useCallback(async (timeoutMs = 3000) => {
    const dc = dataChannelRef.current;
    if (!dc) return false;
    if (dc.readyState === "open") return true;

    return await new Promise((resolve) => {
      let settled = false;
      const timeoutId = setTimeout(() => {
        finish(false);
      }, Math.max(500, timeoutMs));

      const finish = (ok) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        try {
          dc.removeEventListener("open", onOpen);
          dc.removeEventListener("error", onError);
          dc.removeEventListener("close", onClose);
        } catch {}
        resolve(ok);
      };

      const onOpen = () => finish(true);
      const onError = () => finish(false);
      const onClose = () => finish(false);

      dc.addEventListener("open", onOpen);
      dc.addEventListener("error", onError);
      dc.addEventListener("close", onClose);
    });
  }, []);

  const resetRealtimeConnection = useCallback(() => {
    const dc = dataChannelRef.current;
    if (dc) {
      try {
        dc.close();
      } catch {}
    }
    dataChannelRef.current = null;

    const pc = peerConnectionRef.current;
    if (pc) {
      try {
        pc.getSenders().forEach((sender) => sender.track?.stop());
      } catch {}
      try {
        pc.close();
      } catch {}
    }
    peerConnectionRef.current = null;

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    localStreamRef.current = null;
    localAudioTrackRef.current = null;
    setIsAssistantSpeaking(false);
  }, []);

  const cancelAssistantSpeech = useCallback(() => {
    const dc = dataChannelRef.current;
    if (dc && dc.readyState === "open") {
      try {
        dc.send(JSON.stringify({ type: "response.cancel" }));
      } catch {}
    }

    const audioEl = remoteAudioElementRef.current;
    if (audioEl) {
      try {
        audioEl.pause();
        audioEl.currentTime = 0;
      } catch {}
    }

    speechQueueRef.current.forEach((waiter) => {
      if (waiter && typeof waiter.resolve === "function") {
        waiter.resolve(false);
      }
    });
    speechQueueRef.current = [];
    setIsAssistantSpeaking(false);
  }, []);

  const waitForResponseDone = useCallback(async (timeoutMs = 9000) => {
    return await new Promise((resolve) => {
      const waiter = {
        resolve: (ok) => {
          if (waiter.done) return;
          waiter.done = true;
          clearTimeout(waiter.timeoutId);
          resolve(Boolean(ok));
        },
        done: false,
        timeoutId: null,
      };

      waiter.timeoutId = setTimeout(() => {
        const idx = speechQueueRef.current.indexOf(waiter);
        if (idx >= 0) {
          speechQueueRef.current.splice(idx, 1);
        }
        waiter.resolve(false);
      }, Math.max(1200, timeoutMs));

      speechQueueRef.current.push(waiter);
    });
  }, []);

  const waitForRemoteAudioStart = useCallback(
    async (sinceTs, timeoutMs = 2200) => {
      const audioEl = remoteAudioElementRef.current;
      if (!audioEl) return false;
      if (lastRemoteAudioPlayAtRef.current > sinceTs) return true;

      return await new Promise((resolve) => {
        let settled = false;
        const finish = (ok) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          audioEl.removeEventListener("playing", onProgress);
          audioEl.removeEventListener("timeupdate", onProgress);
          resolve(Boolean(ok));
        };

        const onProgress = () => {
          if (
            lastRemoteAudioPlayAtRef.current > sinceTs ||
            audioEl.currentTime > 0
          ) {
            finish(true);
          }
        };

        const timeoutId = setTimeout(
          () => finish(false),
          Math.max(800, timeoutMs),
        );
        audioEl.addEventListener("playing", onProgress);
        audioEl.addEventListener("timeupdate", onProgress);
      });
    },
    [],
  );

  const speakRealtimeText = useCallback(
    async (text) => {
      const cleanText = String(text || "").trim();
      if (!cleanText) return false;

      try {
        await connectRealtimeSession();

        let dc = dataChannelRef.current;
        if (!dc) return false;

        if (dc.readyState !== "open") {
          let opened = await waitForDataChannelOpen(3200);
          if (!opened) {
            resetRealtimeConnection();
            await connectRealtimeSession();
            opened = await waitForDataChannelOpen(3200);
          }
          if (!opened) return false;
          dc = dataChannelRef.current;
          if (!dc || dc.readyState !== "open") return false;
        }

        const sendSpeechRequest = (useInlineInput) => {
          if (!useInlineInput) {
            dc.send(
              JSON.stringify({
                type: "conversation.item.create",
                item: {
                  type: "message",
                  role: "user",
                  content: [
                    {
                      type: "input_text",
                      text: cleanText,
                    },
                  ],
                },
              }),
            );
          }

          dc.send(
            JSON.stringify({
              type: "response.create",
              response: {
                modalities: ["audio"],
                instructions:
                  "Read the text naturally in a warm conversational tone with a slightly brisk pace. Do not add extra words.",
                ...(useInlineInput
                  ? {
                      input: [
                        {
                          type: "input_text",
                          text: cleanText,
                        },
                      ],
                    }
                  : {}),
              },
            }),
          );
        };

        const firstAudioBaseline = lastRemoteAudioPlayAtRef.current;
        sendSpeechRequest(false);
        const firstDone = await waitForResponseDone(1800);
        const firstAudioStarted = await waitForRemoteAudioStart(
          firstAudioBaseline,
          firstDone ? 700 : 1100,
        );

        if (firstDone && firstAudioStarted) {
          return true;
        }

        const secondAudioBaseline = lastRemoteAudioPlayAtRef.current;
        sendSpeechRequest(true);
        const secondDone = await waitForResponseDone(1400);
        const secondAudioStarted = await waitForRemoteAudioStart(
          secondAudioBaseline,
          secondDone ? 700 : 1000,
        );

        return Boolean(secondDone && secondAudioStarted);
      } catch (error) {
        console.error("Realtime speech error:", error);
        return false;
      }
    },
    [
      connectRealtimeSession,
      resetRealtimeConnection,
      waitForDataChannelOpen,
      waitForRemoteAudioStart,
      waitForResponseDone,
    ],
  );

  const startVoiceCapture = useCallback(
    async (onVoiceStart, options = {}) => {
      if (!speechSupported || typeof window === "undefined") {
        alert("Voice search is not supported in this browser");
        return false;
      }

      const maxListenMs = Number(options?.maxListenMs) || 0;
      const emitOnEndOnly = Boolean(options?.emitOnEndOnly);

      isCaptureActiveRef.current = false;
      onVoiceStartRef.current = onVoiceStart;
      endEmitOnlyRef.current = emitOnEndOnly;
      latestTranscriptRef.current = "";
      transcriptDeltaBufferRef.current = "";
      emittedTranscriptRef.current = false;
      suppressPendingResultRef.current = false;

      try {
        await connectRealtimeSession();

        const localTrack = localAudioTrackRef.current;
        if (!localTrack) {
          throw new Error("Realtime audio track not available");
        }

        localTrack.enabled = true;
        isCaptureActiveRef.current = true;

        setIsListening(true);
        setListeningText("Listening...");

        if (listenWindowTimerRef.current) {
          clearTimeout(listenWindowTimerRef.current);
          listenWindowTimerRef.current = null;
        }

        if (maxListenMs > 0) {
          listenWindowTimerRef.current = setTimeout(() => {
            stopVoiceCapture({ suppressPendingResult: false });
          }, maxListenMs);
        }

        if (typeof onVoiceStartRef.current === "function") {
          onVoiceStartRef.current();
          onVoiceStartRef.current = null;
        }
        return true;
      } catch (error) {
        console.error("Realtime voice start failed:", error);
        isCaptureActiveRef.current = false;
        setIsListening(false);
        setListeningText("");
        setIsVoiceQuery(false);
        onVoiceStartRef.current = null;
        const localTrack = localAudioTrackRef.current;
        if (localTrack) {
          localTrack.enabled = false;
        }
        suppressPendingResultRef.current = false;
        endEmitOnlyRef.current = false;

        if (listenWindowTimerRef.current) {
          clearTimeout(listenWindowTimerRef.current);
          listenWindowTimerRef.current = null;
        }
        return false;
      }
    },
    [connectRealtimeSession, speechSupported, stopVoiceCapture],
  );

  const prepareVoiceSession = useCallback(async () => {
    if (!speechSupported || typeof window === "undefined") {
      return false;
    }

    try {
      await connectRealtimeSession();
      return true;
    } catch (error) {
      console.warn("Realtime prewarm failed:", error);
      return false;
    }
  }, [connectRealtimeSession, speechSupported]);

  const handleVoiceSearch = useCallback(
    (onVoiceStart, options = {}) => {
      if (isListening) {
        stopVoiceCapture({ suppressPendingResult: true });
      } else {
        startVoiceCapture(onVoiceStart, options);
      }
    },
    [isListening, startVoiceCapture, stopVoiceCapture],
  );

  const resetVoiceFlags = useCallback(() => {
    setIsVoiceQuery(false);
    setAutoPlayResponse(false);
  }, []);

  return {
    isListening,
    speechSupported,
    isAssistantSpeaking,
    listeningText,
    isVoiceQuery,
    autoPlayResponse,
    setupVoiceRecognition,
    handleVoiceSearch,
    speakRealtimeText,
    primeAudioPlayback,
    prepareVoiceSession,
    cancelAssistantSpeech,
    startVoiceCapture,
    stopVoiceCapture,
    resetVoiceFlags,
  };
};
